import { AnnotationShape } from '@/web-components/import-wizard/typings';
import { checkIsInArea, checkIsInAreaV2 } from './collisionHelper';
import { PrintArea } from '@/web-components/import-wizard-v2/typings/common';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useProductStore } from '@/stores/v2/product';

export function alignAnnotationShape(
  activeShape: AnnotationShape,
  printableArea: PrintArea,
  printableAreaRotation: number,
  alignmentType: string,
  actualWidth: number,
  actualHeight: number,
  polygonDetails: any
): void {
  if (alignmentType === 'align-horizontal-left') {
    activeShape.x = alignLeftControlV2(
      activeShape,
      printableArea,
      printableAreaRotation
    );
  }

  if (alignmentType === 'align-horizontal-right') {
    activeShape.x = alignRightControlV2(
      activeShape,
      actualWidth,
      printableArea,
      printableAreaRotation
    );
  }

  if (alignmentType === 'align-vertical-top') {
    activeShape.y = alignTopControlV2(
      activeShape,
      printableArea,
      printableAreaRotation
    );
  }

  if (alignmentType === 'align-vertical-bottom') {
    activeShape.y = alignBottomControlV2(
      activeShape,
      actualHeight,
      printableArea,
      printableAreaRotation
    );
  }

  if (alignmentType === 'align-vertical-center') {
    if (printableArea.kind === 'Rectangle') {
      activeShape.y =
        printableArea.y + printableArea.height / 2 - actualHeight / 2;
    }
    if (printableArea.kind === 'Ellipse') {
      activeShape.y = printableArea.y - actualHeight / 2;
    }
    if (printableArea.kind === 'Polygon') {
      activeShape.y = polygonDetails.y - actualHeight / 2;
    }
  }

  if (alignmentType === 'align-horizontal-center') {
    if (printableArea.kind === 'Rectangle') {
      activeShape.x =
        printableArea.x + printableArea.width / 2 - actualWidth / 2;
    }
    if (printableArea.kind === 'Ellipse') {
      activeShape.x = printableArea.x - actualWidth / 2;
    }
    if (printableArea.kind === 'Polygon') {
      activeShape.x = polygonDetails.x - actualWidth / 2;
    }
  }
}

export function alignLeftControlV2(
  activeShape: AnnotationShape,
  printableArea: PrintArea,
  printableAreaRotation: number
): number {
  let isInRectangle = false;
  let newX = 0;
  const idx = 2;

  if (printableArea.kind === 'Rectangle') {
    newX = printableArea.x - printableArea.width / 2;
    while (isInRectangle === false) {
      newX = newX + idx;
      isInRectangle = checkIsInAreaV2(
        { ...activeShape, x: newX },
        printableArea,
        printableAreaRotation
      );
    }
    return newX;
  } else if (printableArea.kind === 'Ellipse') {
    let isInEllipse = false;
    let idx = 1;

    while (isInEllipse === false) {
      newX = printableArea.x - printableArea.rx + idx;
      isInEllipse = checkIsInAreaV2(
        { ...activeShape, x: newX },
        printableArea,
        printableAreaRotation
      );
      idx = idx + 1;
    }
    return newX;
  } else if (printableArea.kind === 'Polygon') {
    const leftmostPoint = printableArea.points.reduce((min, current) =>
      current.x < min.x ? current : min
    );

    let isInPolygon = false;
    let idx = 1;
    while (isInPolygon === false) {
      newX = leftmostPoint.x + idx;
      isInPolygon = checkIsInAreaV2(
        { ...activeShape, x: newX },
        printableArea,
        printableAreaRotation
      );
      idx = idx + 1;
    }
    return newX;
  }

  return newX;
}

export function alignRightControlV2(
  activeShape: AnnotationShape,
  actualWidth: number,
  printableArea: PrintArea,
  printableAreaRotation: number
): number {
  let isInRectangle = false;
  const idx = 2;
  let newX = 0;

  if (printableArea.kind === 'Rectangle') {
    newX = printableArea.x + printableArea.width;
    while (isInRectangle === false) {
      newX = newX - idx;
      isInRectangle = checkIsInAreaV2(
        { ...activeShape, x: newX },
        printableArea,
        printableAreaRotation
      );
    }
    return newX;
  } else if (printableArea.kind === 'Ellipse') {
    let isInEllipse = false;
    let idx = 1;

    while (isInEllipse === false) {
      newX = printableArea.x + printableArea.rx * 2 + actualWidth - idx;
      isInEllipse = checkIsInAreaV2(
        { ...activeShape, x: newX },
        printableArea,
        printableAreaRotation
      );
      idx = idx + 1;
    }
    return newX;
  } else if (printableArea.kind === 'Polygon') {
    const rightmostPoint = printableArea.points.reduce((max, current) =>
      current.x > max.x ? current : max
    );

    let isInPolygon = false;
    let idx = 1;
    while (isInPolygon === false) {
      newX = rightmostPoint.x + actualWidth - idx;
      isInPolygon = checkIsInAreaV2(
        { ...activeShape, x: newX },
        printableArea,
        printableAreaRotation
      );
      idx = idx + 1;
    }
    return newX;
  }

  return newX;
}

export function alignTopControlV2(
  activeShape: AnnotationShape,
  printableArea: PrintArea,
  printableAreaRotation: number
): number {
  let newY = 0;

  if (printableArea.kind === 'Rectangle') {
    let isInRectangle = false;
    let idx = 1;
    while (isInRectangle === false) {
      newY = printableArea.y + idx;
      isInRectangle = checkIsInAreaV2(
        {
          ...activeShape,
          y: newY,
        },
        printableArea,
        printableAreaRotation
      );
      idx = idx + 1;
    }
    return newY;
  } else if (printableArea.kind === 'Ellipse') {
    let isInEllipse = false;
    let idx = 1;
    while (isInEllipse === false) {
      newY = printableArea.y - printableArea.ry + idx;
      isInEllipse = checkIsInAreaV2(
        {
          ...activeShape,
          y: newY,
        },
        printableArea,
        printableAreaRotation
      );
      idx = idx + 1;
    }
    return newY;
  } else if (printableArea.kind === 'Polygon') {
    const topmostPoint = printableArea.points.reduce((max, current) =>
      current.y < max.y ? current : max
    );

    let isInPolygon = false;
    let idy = 1;
    while (isInPolygon === false) {
      newY = topmostPoint.y + idy;
      isInPolygon = checkIsInAreaV2(
        { ...activeShape, y: newY },
        printableArea,
        printableAreaRotation
      );
      idy = idy + 1;
    }
    return newY;
  }

  return newY;
}

export function alignBottomControlV2(
  activeShape: AnnotationShape,
  actualHeight: number,
  printableArea: PrintArea,
  printableAreaRotation: number
): number {
  let newY = 0;

  if (printableArea.kind === 'Rectangle') {
    let isInRectangle = false;
    let idx = 1;

    while (isInRectangle === false) {
      newY = printableArea.y + printableArea.height - actualHeight - idx;
      isInRectangle = checkIsInAreaV2(
        {
          ...activeShape,
          y: newY,
        },
        printableArea,
        printableAreaRotation
      );
      idx = idx + 1;
    }
    return newY;
  } else if (printableArea.kind === 'Ellipse') {
    let isInEllipse = false;
    let idx = 1;
    while (isInEllipse === false) {
      newY = printableArea.y + printableArea.ry - actualHeight - idx;
      isInEllipse = checkIsInAreaV2(
        {
          ...activeShape,
          y: newY,
        },
        printableArea,
        printableAreaRotation
      );
      idx = idx + 1;
    }
    return newY;
  } else if (printableArea.kind === 'Polygon') {
    const topmostPoint = printableArea.points.reduce((max, current) =>
      current.y > max.y ? current : max
    );

    let isInPolygon = false;
    let idy = 1;
    while (isInPolygon === false) {
      newY = topmostPoint.y + actualHeight - idy;
      isInPolygon = checkIsInAreaV2(
        { ...activeShape, y: newY },
        printableArea,
        printableAreaRotation
      );
      idy = idy + 1;
    }
    return newY;
  }

  return newY;
}

export function alignLeftControl(
  rectLineWidth: number,
  activeShape: AnnotationShape,
  printableArea: PrintArea,
  printableAreaRotation: number
): number {
  let isInRectangle = false;
  let newX = 0;
  const idx = 2;

  if (printableArea.kind === 'Rectangle') {
    newX = printableArea.x - printableArea.width / 2;
    while (isInRectangle === false) {
      newX = newX + idx;
      isInRectangle = checkIsInArea(
        { ...activeShape, x: newX },
        printableArea,
        printableArea.kind,
        printableAreaRotation,
        rectLineWidth
      );
    }
    return newX;
  } else if (printableArea.kind === 'Ellipse') {
    let isInEllipse = false;
    let idx = 1;

    while (isInEllipse === false) {
      newX = printableArea.x - printableArea.rx + idx;
      isInEllipse = checkIsInArea(
        { ...activeShape, x: newX },
        printableArea,
        printableArea.kind,
        printableAreaRotation,
        rectLineWidth
      );
      idx = idx + 1;
    }
    return newX;
  }

  return newX;
}

export function alignRightControl(
  rectLineWidth: number,
  activeShape: AnnotationShape,
  actualWidth: number,
  printableArea: PrintArea,
  printableAreaRotation: number
): number {
  let isInRectangle = false;
  const idx = 2;
  let newX = 0;

  if (printableArea.kind === 'Rectangle') {
    newX = printableArea.x + printableArea.width;
    while (isInRectangle === false) {
      newX = newX - idx;
      isInRectangle = checkIsInArea(
        { ...activeShape, x: newX },
        printableArea,
        printableArea.kind,
        printableAreaRotation,
        rectLineWidth
      );
    }
    return newX;
  } else if (printableArea.kind === 'Ellipse') {
    let isInEllipse = false;
    let idx = 1;

    while (isInEllipse === false) {
      newX = printableArea.x + printableArea.rx * 2 + actualWidth - idx;
      isInEllipse = checkIsInArea(
        { ...activeShape, x: newX },
        printableArea,
        printableArea.kind,
        printableAreaRotation,
        rectLineWidth
      );
      idx = idx + 1;
    }
    return newX;
  }

  return newX;
}

export function alignTopControl(
  rectLineWidth: number,
  activeShape: AnnotationShape,
  printableArea: PrintArea,
  printableAreaRotation: number
): number {
  let newY = 0;

  if (printableArea.kind === 'Rectangle') {
    let isInRectangle = false;
    let idx = 1;

    while (isInRectangle === false) {
      newY = printableArea.y + idx;
      isInRectangle = checkIsInArea(
        {
          ...activeShape,
          y: newY,
        },
        printableArea,
        printableArea.kind,
        printableAreaRotation,
        rectLineWidth
      );
      idx = idx + 1;
    }
    return newY;
  } else if (printableArea.kind === 'Ellipse') {
    let isInEllipse = false;
    let idx = 1;
    while (isInEllipse === false) {
      newY = printableArea.y - printableArea.ry + idx;
      isInEllipse = checkIsInArea(
        {
          ...activeShape,
          y: newY,
        },
        printableArea,
        printableArea.kind,
        printableAreaRotation,
        rectLineWidth
      );
      idx = idx + 1;
    }
    return newY;
  }

  return newY;
}

export function alignBottomControl(
  rectLineWidth: number,
  activeShape: AnnotationShape,
  actualHeight: number,
  printableArea: PrintArea,
  printableAreaRotation: number
): number {
  let newY = 0;

  if (printableArea.kind === 'Rectangle') {
    let isInRectangle = false;
    let idx = 1;

    while (isInRectangle === false) {
      newY = printableArea.y + printableArea.height - actualHeight - idx;
      isInRectangle = checkIsInArea(
        {
          ...activeShape,
          y: newY,
        },
        printableArea,
        printableArea.kind,
        printableAreaRotation,
        rectLineWidth
      );
      idx = idx + 1;
    }
    return newY;
  } else if (printableArea.kind === 'Ellipse') {
    let isInEllipse = false;
    let idx = 1;
    while (isInEllipse === false) {
      newY = printableArea.y + printableArea.ry - actualHeight - idx;
      isInEllipse = checkIsInArea(
        {
          ...activeShape,
          y: newY,
        },
        printableArea,
        printableArea.kind,
        printableAreaRotation,
        rectLineWidth
      );
      idx = idx + 1;
    }
    return newY;
  }

  return newY;
}

export function alignRoundItemAnnotationShape(
  shape: any,
  panelArea: any,
  alignmentType: string,
  printableArea: any,
  printableAreaRotation: number
) {
  if (alignmentType === 'align-horizontal-left' && shape !== undefined) {
    if (printableArea.kind === 'Rectangle') {
      if (printableAreaRotation === 0 && shape.rotation === 0) {
        shape.x = panelArea.x;
      } else {
        shape.x = alignLeftControlV2(
          shape,
          {
            ...printableArea,
            x: panelArea.x,
            width: panelArea.width,
          },
          printableAreaRotation
        );
      }
    }
  } else if (
    alignmentType === 'align-horizontal-right' &&
    shape !== undefined
  ) {
    if (printableAreaRotation === 0 && shape.rotation === 0) {
      shape.x = panelArea.x + panelArea.width - shape.width;
    } else {
      shape.x = alignRightControlV2(
        shape,
        shape.width,
        {
          ...printableArea,
          x: panelArea.x,
          width: panelArea.width,
        },
        printableAreaRotation
      );
    }
  } else if (alignmentType === 'align-vertical-top') {
    if (printableArea.kind === 'Rectangle') {
      if (printableAreaRotation === 0 && shape.rotation === 0) {
        shape.y = printableArea.y;
      } else {
        shape.y = alignTopControlV2(
          shape,
          {
            ...printableArea,
            x: panelArea.x,
            width: panelArea.width,
          },
          printableAreaRotation
        );
      }
    }
  } else if (alignmentType === 'align-vertical-bottom') {
    if (printableArea.kind === 'Rectangle') {
      if (printableAreaRotation === 0 && shape.rotation === 0) {
        shape.y = printableArea.y + printableArea.height - shape.height;
      } else {
        shape.y = alignBottomControlV2(
          shape,
          shape.height,
          {
            ...printableArea,
            x: panelArea.x,
            width: panelArea.width,
          },
          printableAreaRotation
        );
      }
    }
  }
  if (alignmentType === 'align-vertical-center') {
    if (printableArea.kind === 'Rectangle') {
      shape.y = printableArea.y + printableArea.height / 2 - shape.height / 2;
    }
  }

  if (alignmentType === 'align-horizontal-center') {
    if (printableArea.kind === 'Rectangle') {
      shape.x = panelArea.x + panelArea.width / 2 - shape.width / 2;
    }
  }

  return shape;
}

export const isLeftAligned = (
  areaX: number,
  shapeX: number,
  alignmentMargin: number
): boolean => Math.abs(areaX - shapeX) < alignmentMargin;

export const isRightAligned = (
  areaX: number,
  areaWidth: number,
  shapeX: number,
  shapeWidth: number,
  alignmentMargin: number
): boolean =>
  Math.abs(areaX + areaWidth - (shapeX + shapeWidth)) < alignmentMargin;

export const isTopAligned = (
  areaY: number,
  shapeY: number,
  alignmentMargin: number
): boolean => Math.abs(areaY - shapeY) < alignmentMargin;

export const isBottomAligned = (
  areaY: number,
  areaHeight: number,
  shapeY: number,
  shapeHeight: number,
  alignmentMargin: number
): boolean =>
  Math.abs(areaY + areaHeight - (shapeY + shapeHeight)) < alignmentMargin;

export function isAlignButtonDisabled(align: string): boolean | undefined {
  if (
    align !== 'align-horizontal-center' &&
    align !== 'align-vertical-center'
  ) {
    const shapeStore = useShapeStore();
    const productStore = useProductStore();
    const activeShape = shapeStore.shapeById(shapeStore.activeShapeId);
    const scaledArea = productStore.printPositionArea(
      productStore.activePosition.id || '',
      true
    );

    if (activeShape && !activeShape.isInArea) {
      return true;
    } else if (activeShape?.isInArea && scaledArea?.kind === 'Rectangle') {
      const {
        x: shapeX,
        y: shapeY,
        width: shapeWidth,
        height: shapeHeight,
      } = activeShape;
      const {
        x: areaX,
        y: areaY,
        width: areaWidth,
        height: areaHeight,
      } = scaledArea;
      const alignmentMargin = 2;

      switch (align) {
        case 'align-horizontal-left':
          return isLeftAligned(areaX, shapeX, alignmentMargin);

        case 'align-horizontal-right':
          return isRightAligned(
            areaX,
            areaWidth,
            shapeX,
            shapeWidth,
            alignmentMargin
          );

        case 'align-vertical-top':
          return isTopAligned(areaY, shapeY, alignmentMargin);

        case 'align-vertical-bottom':
          return isBottomAligned(
            areaY,
            areaHeight,
            shapeY,
            shapeHeight,
            alignmentMargin
          );

        default:
          return false;
      }
    } else {
      return false;
    }
  }
}
